/**
 * Login screen — email + password sign-in ONLY (production hardening).
 *
 * AVOS authenticates with an email address and a password. There is NO
 * third-party / social sign-in (Google, Apple, Microsoft, …) and NO public
 * sign-up or trial path on this screen — those surfaces have been removed, not
 * merely hidden, so the rendered HTML offers exactly one way in. The screen
 * surfaces every sign-in outcome as an accessible {@link UserMessage}
 * (empty-field validation, malformed email, wrong credentials, locked,
 * suspended, and a generic server failure).
 *
 * PRESENTATION (premium SaaS auth): a two-column layout — a restrained emerald
 * marketing panel on the left (brand lockup + headline + the AVOS arch motif)
 * and the sign-in form on the warm-ivory right column. Below the tablet
 * breakpoint the marketing panel is hidden and the form becomes a single,
 * comfortably-spaced column. All styling lives in the shared shell stylesheet
 * (`styles/stylesheet.ts`, the `AUTH_PAGE_CSS` block) using AVOS design tokens
 * — no inline styles, no browser-default controls.
 *
 * Web boundary: this screen NEVER imports the backend Identity_Service at
 * runtime. The password sign-in is supplied as an injected `onLogin` callback —
 * the web package may consume shared TYPES only (enforced by the boundary lint
 * rule). The screen registry (`screens.tsx`) renders it with no props for SSR,
 * so every prop is optional and the screen renders its full layout, fields, and
 * controls without any injected service.
 *
 * All user-facing text resolves through the i18n catalog via `useT()` (Req
 * 22.11): the screen references stable message ids, never hardcoded literals.
 *
 * SSR-friendly: the initial render (no `result`) is a pure function of props, so
 * `renderToString` produces the complete form. Interactivity (field state,
 * show/hide password, submit, busy state) activates after hydration; a JS-free
 * client still gets a real, labelled, submittable form.
 */
import { useId, useState, type FormEvent, type ReactElement } from "react";
import { useT } from "../i18n/index.js";
import { ArchMark } from "../console/ArchMark.js";
import {
  UserMessage,
  type UserMessageModel,
} from "../components/UserMessage.js";

/**
 * The distinct authentication outcomes the login screen surfaces (Req 22.10).
 * `ok` establishes a session (the screen shows no error); the remaining values
 * each map to a specific User_Message. There is deliberately no social/OAuth
 * outcome — email + password is the only mechanism.
 *
 * `emailRequired`, `emailInvalid`, and `passwordRequired` are produced by the
 * screen's own client-side validation BEFORE any network call; the rest are
 * returned by the injected `onLogin` after talking to the server.
 */
export type LoginResult =
  | "ok"
  | "emailRequired"
  | "emailInvalid"
  | "passwordRequired"
  | "invalidCredentials"
  | "emailUnverified"
  | "accountLocked"
  | "accountSuspended"
  | "serverError";

/** The credentials a password sign-in submits to the Identity_Service. */
export interface LoginCredentials {
  readonly email: string;
  readonly password: string;
}

/** Props for {@link LoginScreen}. All optional so the screen renders standalone. */
export interface LoginScreenProps {
  /**
   * Injected Identity_Service password sign-in. Receives the entered
   * credentials and resolves to a {@link LoginResult}. Omitted in SSR / the
   * screen registry, where the form renders but does not submit.
   */
  readonly onLogin?: (credentials: LoginCredentials) => Promise<LoginResult>;
  /**
   * An initial result to render (e.g. a server-provided outcome after a
   * redirect). Lets the screen show a message on first paint without client JS.
   */
  readonly initialResult?: LoginResult;
  /**
   * When `true`, render the "Forgot password?" link. The link is only shown
   * where the password-reset flow is actually wired, so the UI never advertises
   * a dead end. Defaults to `true`.
   */
  readonly showForgotPassword?: boolean;
}

/** The non-`ok` results that carry a User_Message, and their catalog id prefix. */
const RESULT_MESSAGE_KEYS = {
  emailRequired: "auth.login.emailRequired",
  emailInvalid: "auth.login.emailInvalid",
  passwordRequired: "auth.login.passwordRequired",
  invalidCredentials: "auth.login.invalidCredentials",
  emailUnverified: "auth.login.emailUnverified",
  accountLocked: "auth.login.accountLocked",
  accountSuspended: "auth.login.accountSuspended",
  serverError: "auth.login.serverError",
} as const;

/** Field-level results that should mark a specific input as invalid. */
const EMAIL_ERROR_RESULTS = new Set<LoginResult>(["emailRequired", "emailInvalid"]);
const PASSWORD_ERROR_RESULTS = new Set<LoginResult>(["passwordRequired"]);

/**
 * A pragmatic, non-strict email shape check used purely for inline UX feedback
 * (the server remains the source of truth). Requires a single `@` with
 * non-empty, dot-containing-optional local/domain parts and no spaces.
 */
function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** An eye glyph (open/closed) for the integrated show/hide-password control. */
function EyeIcon({ off }: { readonly off: boolean }): ReactElement {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {off ? <line x1="3" y1="3" x2="21" y2="21" /> : null}
    </svg>
  );
}

/** A small inline spinner for the submit button's loading state. */
function Spinner(): ReactElement {
  return (
    <svg
      className="auth-spinner"
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.3" strokeWidth="2.5" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

export default function LoginScreen({
  onLogin,
  initialResult,
  showForgotPassword = true,
}: LoginScreenProps = {}): ReactElement {
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<LoginResult | undefined>(initialResult);

  const errorId = useId();

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    // Guard against a duplicate submit while a sign-in is already in flight.
    if (busy || onLogin === undefined) {
      return;
    }

    // Client-side validation first — fast, offline-capable feedback (Req 22.10)
    // that avoids a pointless round-trip for obviously-incomplete input.
    const trimmedEmail = email.trim();
    if (trimmedEmail === "") {
      setResult("emailRequired");
      return;
    }
    if (!looksLikeEmail(trimmedEmail)) {
      setResult("emailInvalid");
      return;
    }
    if (password === "") {
      setResult("passwordRequired");
      return;
    }

    setBusy(true);
    try {
      const outcome = await onLogin({ email: trimmedEmail, password });
      setResult(outcome);
    } catch {
      // A thrown callback (network/unexpected failure) is a server-side problem
      // from the user's perspective — never leak the raw error.
      setResult("serverError");
    } finally {
      setBusy(false);
    }
  }

  const message = toLoginMessage(result, t);
  const emailInvalid = result !== undefined && EMAIL_ERROR_RESULTS.has(result);
  const passwordInvalid = result !== undefined && PASSWORD_ERROR_RESULTS.has(result);
  const describedBy = message !== undefined ? errorId : undefined;

  return (
    <section
      aria-labelledby="screen-heading"
      data-screen="login"
      className="auth-screen auth-screen--login auth-page"
    >
      {/* LEFT: restrained emerald marketing panel (hidden below tablet). The
          arch motif is pure CSS/SVG — no external imagery. Decorative, so it is
          hidden from assistive tech. */}
      <aside className="auth-aside" aria-hidden="true">
        <div className="auth-aside__motif" />
        <div className="auth-aside__top">
          <span className="auth-aside__brand">
            <ArchMark size={26} baselineColor="#101828" />
            <span className="auth-aside__brand-name">AVOS</span>
          </span>
          <span className="auth-aside__endorse">
            {t("auth.login.brandEndorsement")}
          </span>
        </div>
        <div className="auth-aside__pitch">
          <h2 className="auth-aside__headline">
            {t("auth.login.marketingHeadline")}
          </h2>
          <p className="auth-aside__body">{t("auth.login.marketingBody")}</p>
        </div>
        <p className="auth-aside__foot">{t("auth.login.marketingFooter")}</p>
      </aside>

      {/* RIGHT: the sign-in form column on warm ivory. */}
      <div className="auth-main">
        <div className="auth-panel" data-testid="login-card">
          {/* Compact brand lockup — primarily for the single-column mobile
              layout where the marketing panel is hidden. */}
          <span className="auth-panel__brand">
            <ArchMark size={22} baselineColor="#14402F" />
            <span className="auth-panel__brand-name">AVOS</span>
          </span>

          <h1 id="screen-heading" className="auth-panel__title">
            {t("auth.login.title")}
          </h1>
          <p className="auth-panel__prompt">{t("auth.login.prompt")}</p>

          {message !== undefined ? (
            <div className="auth-panel__alert" id={errorId}>
              <UserMessage message={message} severity="error" />
            </div>
          ) : null}

          <form
            onSubmit={handleSubmit}
            noValidate
            data-testid="login-form"
            className="auth-form"
          >
            <div className="auth-field">
              <label htmlFor="login-email">{t("auth.login.emailLabel")}</label>
              <input
                id="login-email"
                name="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                required
                disabled={busy}
                aria-invalid={emailInvalid || undefined}
                aria-describedby={describedBy}
                value={email}
                onChange={(e): void => setEmail(e.target.value)}
              />
            </div>

            <div className="auth-field">
              <label htmlFor="login-password">
                {t("auth.login.passwordLabel")}
              </label>
              <div className="auth-field__control">
                <input
                  id="login-password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  disabled={busy}
                  aria-invalid={passwordInvalid || undefined}
                  aria-describedby={describedBy}
                  value={password}
                  onChange={(e): void => setPassword(e.target.value)}
                />
                <button
                  type="button"
                  className="auth-field__reveal"
                  data-testid="login-password-toggle"
                  aria-pressed={showPassword}
                  aria-controls="login-password"
                  aria-label={
                    showPassword
                      ? t("auth.login.hidePassword")
                      : t("auth.login.showPassword")
                  }
                  title={
                    showPassword
                      ? t("auth.login.hidePassword")
                      : t("auth.login.showPassword")
                  }
                  disabled={busy}
                  onClick={(): void => setShowPassword((v) => !v)}
                >
                  <EyeIcon off={showPassword} />
                </button>
              </div>
            </div>

            {showForgotPassword ? (
              <div className="auth-form__meta">
                <a href="/forgot-password" data-testid="login-forgot-password">
                  {t("auth.login.forgotPassword")}
                </a>
              </div>
            ) : null}

            <button
              type="submit"
              data-testid="login-submit"
              className="auth-form__submit"
              disabled={busy}
              aria-busy={busy}
            >
              {busy ? (
                <span className="auth-form__submit-busy">
                  <Spinner />
                  {t("auth.login.submitBusy")}
                </span>
              ) : (
                t("auth.login.submit")
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

/**
 * Build the {@link UserMessageModel} for a non-`ok` login result by resolving the
 * matching catalog ids (what/why/next). Returns `undefined` when there is no
 * result yet or the result is `"ok"` — the success path shows no error message.
 * Pure and SSR-safe.
 */
export function toLoginMessage(
  result: LoginResult | undefined,
  t: (id: string) => string,
): UserMessageModel | undefined {
  if (result === undefined || result === "ok") {
    return undefined;
  }
  const prefix = RESULT_MESSAGE_KEYS[result];
  return {
    what: t(`${prefix}.what`),
    why: t(`${prefix}.why`),
    next: t(`${prefix}.next`),
  };
}
